const ACCESS_TOKEN_KEY = 'auth.access_token';

export const DEFAULT_ACCESS_TOKEN_FALLBACK_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

type StoredAccessTokenRecord = {
  token: string;
  storedAt: number;
  tokenExpMs?: number;
};

/**
 * Access token fallback storage (localStorage + TTL).
 *
 * This is a pragmatic workaround for backend refresh requirements:
 * `/api/auth/refresh_token` requires an access token even if it's expired.
 *
 * We keep the last received access token in `localStorage` (with TTL) to be able to send
 * it in the `Authorization` header when the `access_token` HttpOnly cookie
 * disappears in long-running sessions.
 *
 * Security note:
 * - Storing tokens in Web Storage increases the impact of XSS.
 * - We apply a TTL (and clear on logout) to reduce the risk of keeping old tokens around.
 */
export function getStoredAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const maxAgeMs = getMaxAgeMs();

    // Preferred: localStorage (persistent across reloads).
    const rawLocal = window.localStorage.getItem(ACCESS_TOKEN_KEY);
    if (rawLocal) {
      const tokenFromLocal = readTokenFromRaw(rawLocal, maxAgeMs, 'local');
      if (tokenFromLocal) return tokenFromLocal;
    }

    // Migration path: if a previous version stored the token in sessionStorage, move it over.
    const rawSession = window.sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (rawSession) {
      const token = migrateSessionToLocal(rawSession, maxAgeMs);
      return token;
    }

    return null;
  } catch {
    return null;
  }
}

export function setStoredAccessToken(token: string, tokenExpMs?: number): void {
  if (typeof window === 'undefined') return;
  try {
    if (!token || token.trim().length === 0) return;
    const record: StoredAccessTokenRecord = {
      token,
      storedAt: Date.now(),
      tokenExpMs: typeof tokenExpMs === 'number' ? tokenExpMs : tryExtractTokenExpMs(token),
    };
    window.localStorage.setItem(ACCESS_TOKEN_KEY, JSON.stringify(record));
  } catch {
    // Best-effort: ignore storage failures.
  }
}

export function clearStoredAccessToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    // Cleanup any legacy location as well.
    window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // Best-effort: ignore storage failures.
  }
}

function getMaxAgeMs(): number {
  const raw =
    (import.meta as unknown as {
      env?: { VITE_ACCESS_TOKEN_FALLBACK_MAX_AGE_MS?: string };
    }).env?.VITE_ACCESS_TOKEN_FALLBACK_MAX_AGE_MS ?? '';

  const parsed = Number.parseInt(String(raw), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_ACCESS_TOKEN_FALLBACK_MAX_AGE_MS;
  }

  // Safety cap: don't keep fallback tokens indefinitely.
  const capMs = 7 * 24 * 60 * 60 * 1000; // 7 days
  return Math.min(parsed, capMs);
}

function isLikelyJwt(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts.every(p => p.trim().length > 0);
}

function readTokenFromRaw(
  raw: string,
  maxAgeMs: number,
  source: 'local' | 'session'
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    if (source === 'local') window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    if (source === 'session') window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    return null;
  }

  // Preferred JSON format.
  try {
    const parsed = JSON.parse(trimmed) as Partial<StoredAccessTokenRecord>;
    if (parsed && typeof parsed === 'object') {
      const token = typeof parsed.token === 'string' ? parsed.token.trim() : '';
      const storedAt = typeof parsed.storedAt === 'number' ? parsed.storedAt : 0;
      if (!token || !Number.isFinite(storedAt) || storedAt <= 0) {
        if (source === 'local') window.localStorage.removeItem(ACCESS_TOKEN_KEY);
        if (source === 'session') window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        return null;
      }

      if (Date.now() - storedAt > maxAgeMs) {
        if (source === 'local') window.localStorage.removeItem(ACCESS_TOKEN_KEY);
        if (source === 'session') window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        return null;
      }

      return token;
    }
  } catch {
    // fallthrough to legacy format handling below
  }

  // Legacy format: plain token string (only accept if it looks like a JWT).
  if (!isLikelyJwt(trimmed)) {
    if (source === 'local') window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    if (source === 'session') window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    return null;
  }

  // Best-effort migration to JSON format (if local).
  if (source === 'local') {
    setStoredAccessToken(trimmed);
  }

  return trimmed;
}

function migrateSessionToLocal(rawSession: string, maxAgeMs: number): string | null {
  const token = readTokenFromRaw(rawSession, maxAgeMs, 'session');
  // If it was valid, persist it to localStorage and remove from sessionStorage.
  if (token) {
    setStoredAccessToken(token);
  }
  try {
    window.sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // ignore
  }
  return token;
}

function tryExtractTokenExpMs(token: string): number | undefined {
  try {
    if (!isLikelyJwt(token)) return undefined;
    const payload = token.split('.')[1];
    const decodedPayload = base64UrlDecode(payload);
    const decoded = JSON.parse(decodedPayload) as { exp?: number };
    if (typeof decoded?.exp === 'number' && Number.isFinite(decoded.exp)) {
      return decoded.exp * 1000;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function base64UrlDecode(input: string): string {
  // Convert base64url -> base64
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return atob(padded);
}

